/**
 * Theme wishlist — stores product handles in localStorage.
 * No app, no external scripts, no backend.
 *
 * Markup contract:
 *   Button : <button class="js-wishlist-btn" data-product-handle="..."> ... </button>
 *   Count  : any element with class "js-wishlist-count"
 *   Page   : <div class="js-wishlist-grid" data-empty-target=".js-wishlist-empty">
 */

class ThemeWishlist {
  constructor() {
    this.storageKey = 'hhc-wishlist';
    this.addedClass = 'is-in-wishlist';
    this.items = this.read();

    this.bindEvents();
    this.syncButtons();
    this.updateCount();
    this.renderPage();
  }

  /* ---------- storage ---------- */

  read() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? [...new Set(parsed)] : [];
    } catch (e) {
      return [];
    }
  }

  save() {
    try {
      this.items = [...new Set(this.items)];
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    } catch (e) {
      // Storage can be unavailable (private mode, quota). Fail quietly.
    }
  }

  has(handle) {
    return this.items.indexOf(handle) !== -1;
  }

  toggle(handle) {
    if (!handle) return false;
    if (this.has(handle)) {
      this.items = this.items.filter((h) => h !== handle);
    } else {
      this.items.push(handle);
    }
    this.save();
    return this.has(handle);
  }

  remove(handle) {
    this.items = this.items.filter((h) => h !== handle);
    this.save();
  }

  /* ---------- events ---------- */

  bindEvents() {
    document.addEventListener('click', (evt) => {
      const btn = evt.target.closest('.js-wishlist-btn');
      if (btn) {
        evt.preventDefault();
        evt.stopPropagation();
        const handle = btn.dataset.productHandle;
        const nowIn = this.toggle(handle);
        this.setButtonState(btn, nowIn);
        this.syncButtons();
        this.updateCount();

        // On the wishlist page, drop the card out straight away.
        const card = btn.closest('.js-wishlist-item');
        if (card && !nowIn) {
          card.remove();
          this.checkEmpty();
        }
        return;
      }

      const removeBtn = evt.target.closest('.js-wishlist-remove');
      if (removeBtn) {
        evt.preventDefault();
        const handle = removeBtn.dataset.productHandle;
        this.remove(handle);
        const card = removeBtn.closest('.js-wishlist-item');
        if (card) card.remove();
        this.syncButtons();
        this.updateCount();
        this.checkEmpty();
      }
    });

    // Keep state correct when the theme re-renders sections (filters, pagination,
    // quick add, cart drawer) — those replace markup without a page load.
    const observer = new MutationObserver(window.debounce
      ? window.debounce(() => this.syncButtons(), 150)
      : () => this.syncButtons());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- button state ---------- */

  setButtonState(btn, isIn) {
    btn.classList.toggle(this.addedClass, isIn);
    btn.setAttribute('aria-pressed', isIn ? 'true' : 'false');
    const label = btn.dataset[isIn ? 'labelRemove' : 'labelAdd'];
    if (label) btn.setAttribute('aria-label', label);
  }

  syncButtons() {
    document.querySelectorAll('.js-wishlist-btn').forEach((btn) => {
      this.setButtonState(btn, this.has(btn.dataset.productHandle));
    });
  }

  updateCount() {
    const count = this.items.length;
    document.querySelectorAll('.js-wishlist-count').forEach((el) => {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
    document.documentElement.classList.toggle('wishlist-has-items', count > 0);
  }

  /* ---------- wishlist page ---------- */

  async renderPage() {
    const grid = document.querySelector('.js-wishlist-grid');
    if (!grid) return;

    if (!this.items.length) {
      this.checkEmpty();
      return;
    }

    const results = await Promise.all(
      this.items.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}?view=wishlist-card`);
          if (!res.ok) return null;
          const html = await res.text();
          return { handle, html };
        } catch (e) {
          return null;
        }
      })
    );

    // Any handle that no longer resolves (deleted or unpublished product)
    // is dropped from storage so it doesn't linger forever.
    const missing = [];

    results.forEach((result) => {
      if (!result || !result.html.trim()) {
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.className = 'js-wishlist-item wishlist-item relative';
      wrapper.innerHTML = result.html;

      if (!wrapper.querySelector('.card')) {
        missing.push(result.handle);
        return;
      }

      wrapper.appendChild(this.buildRemoveButton(result.handle));
      grid.appendChild(wrapper);
    });

    this.items
      .filter((h) => !results.some((r) => r && r.handle === h))
      .forEach((h) => missing.push(h));

    if (missing.length) {
      this.items = this.items.filter((h) => missing.indexOf(h) === -1);
      this.save();
      this.updateCount();
    }

    this.checkEmpty();
    this.syncButtons();
  }

  buildRemoveButton(handle) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'js-wishlist-remove wishlist-remove';
    btn.dataset.productHandle = handle;
    btn.setAttribute('aria-label', 'Remove from wishlist');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 18L18 6M6 6l12 12"/></svg>';
    return btn;
  }

  checkEmpty() {
    const grid = document.querySelector('.js-wishlist-grid');
    const empty = document.querySelector('.js-wishlist-empty');
    if (!grid || !empty) return;
    const isEmpty = grid.children.length === 0;
    grid.toggleAttribute('hidden', isEmpty);
    empty.toggleAttribute('hidden', !isEmpty);
  }
}

if (!window.ThemeWishlistInstance) {
  const start = () => {
    window.ThemeWishlistInstance = new ThemeWishlist();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}